// client/src/pages/sections/groups/MarkNlcButton.js
// Per-MemberCard "Mark NLC" affordance. Opens a confirmation overlay; if
// the client plays a structural role in its SG/RG (parent / anchor with
// other members), surfaces the sub-resolution prompts per
// CLIENT_GROUPS_STATUS.md §3.1:
//
//   SG parent? partner picks promote-child | disband | move-to-other
//   RG anchor? partner picks promote-SG    | disband | merge-with-other
//
// Both sections render only when applicable. Standalone or child-member
// clients still see the original one-click confirm.
//
// Context comes from /api/dgNlcContext/{clientCode} (lazy, on open).
// Mutation POSTs to /api/diResolveAction with the combined payload.

import { useEffect, useRef, useState } from 'react';
import { loadAnchors, getAnchors } from './clientRosterCache';
import GroupAnchorPicker from './GroupAnchorPicker';
import { useOptimistic } from './optimisticContext';
import { emitToast, dismissToast, errorText } from './toast';
import { postResolveAction, getActionPeriod } from './resolveActionQueue';
import { markRelatedParties } from '../disengagementApi';
import DisengagementFields from '../DisengagementFields';
import { EMPTY_DISENGAGEMENT, validateDisengagement } from '../disengagementOptions';
import '../Disengagement.css';
import ClientChip from './ClientChip';
import { useSubmitGuard } from './useSubmitGuard';

const CLIENT_CODE_RE = /^\d{5}-\d{3}$/;
const CODE_RE = /(\d{5}-\d{3})/;

export default function MarkNlcButton({ member, onResolved, testMode, testSessionId, keepGroupVisible }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [ctx,         setCtx]         = useState(null);    // /api/dgNlcContext payload
  const [ctxErr,      setCtxErr]      = useState(null);
  // Optimistic flow closes the modal immediately, so there's no live
  // submitting state to toggle — `submitting` stays false and is kept
  // only because the (now-unmounted-on-submit) controls reference it.
  const [submitting]                  = useState(false);
  const guard                         = useSubmitGuard();   // one submit at a time: a double-click or a re-click mid-wait is ignored
  const [error,       setError]       = useState(null);
  const { dismissByClientCode, restoreIds, markPending, clearPending } = useOptimistic();
  // Close on backdrop click only when the press STARTED on the backdrop, so a
  // press that begins inside the modal and releases on the overlay (e.g. the
  // modal resized under the cursor) doesn't wrongly dismiss it.
  const overlayDownRef = useRef(false);

  // Anchor lists for the GroupAnchorPicker (existing SG / RG parents
  // only). Sourced from the shared module-level cache primed at section
  // mount, so the picker opens instantly.
  const [sgAnchors, setSgAnchors] = useState(() => (getAnchors()?.sgAnchors) || []);
  const [rgAnchors, setRgAnchors] = useState(() => (getAnchors()?.rgAnchors) || []);

  // Sub-resolution form state.
  const [sgResolution, setSgResolution] = useState(null);    // 'promote'|'disband'|'move'
  const [newSgParent,  setNewSgParent]  = useState('');
  const [rgResolution, setRgResolution] = useState(null);    // 'promote'|'disband'|'merge'
  const [newRgAnchor,  setNewRgAnchor]  = useState('');

  // Disengagement follow-up answers (collected for every NLC mark).
  const [disengagement, setDisengagement] = useState(EMPTY_DISENGAGEMENT);
  const [disengErrors,  setDisengErrors]  = useState({});

  // Close on Esc.
  useEffect(() => {
    if (!showConfirm) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showConfirm, submitting]);

  // Load context + (refresh, just in case) the anchor cache when the
  // dialog opens. Anchor cache is usually already warm from the section
  // mount; the call is a no-op when cached.
  useEffect(() => {
    if (!showConfirm || !member.code) return undefined;
    let cancelled = false;
    setLoading(true);
    setCtxErr(null);
    setCtx(null);
    Promise.all([
      fetch(`/api/dgNlcContext/${encodeURIComponent(member.code)}`, { credentials: 'include' })
        .then(async (r) => {
          if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
          return r.json();
        }),
      loadAnchors().catch(() => ({ sgAnchors: [], rgAnchors: [] })),
    ])
      .then(([data, anchors]) => {
        if (cancelled) return;
        setCtx(data);
        // Only ONE candidate left: no election — they become the new parent / anchor (the partner may still pick
        // another option; the server applies the same default when none is sent).
        if (data.isSgParent && (data.sgChildren || []).length === 1) {
          setSgResolution('promote');
          setNewSgParent(data.sgChildren[0].clientCode);
        }
        if (data.isRgAnchor && (data.rgSiblingSgs || []).length === 1) {
          setRgResolution('promote');
          setNewRgAnchor(data.rgSiblingSgs[0].sgCode);
        }
        setSgAnchors(anchors.sgAnchors || []);
        setRgAnchors(anchors.rgAnchors || []);
      })
      .catch((e) => { if (!cancelled) setCtxErr(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [showConfirm, member.code]);

  function close() {
    setShowConfirm(false);
    setSgResolution(null);
    setNewSgParent('');
    setRgResolution(null);
    setNewRgAnchor('');
    setDisengagement(EMPTY_DISENGAGEMENT);
    setDisengErrors({});
    setError(null);
  }

  // Extract a #####-### code from picker-formatted "code — name" values.
  function pickerCode(s) {
    const m = (s || '').match(CODE_RE);
    return m ? m[1] : '';
  }

  function validate() {
    if (!ctx) return 'Loading context — try again in a moment.';
    if (ctx.isSgParent) {
      if (!sgResolution) return 'Pick what should happen to the Signer Group.';
      if (sgResolution === 'promote' && !newSgParent) return 'Pick the child to promote as the new Signer Group parent.';
      if (sgResolution === 'move' && !CLIENT_CODE_RE.test(pickerCode(newSgParent))) {
        return 'Pick a target Signer Group from the list.';
      }
    }
    if (ctx.isRgAnchor) {
      if (!rgResolution) return 'Pick what should happen to the Relationship Group.';
      if (rgResolution === 'promote' && !newRgAnchor) return 'Pick the Signer Group to promote as the new Relationship Group anchor.';
      if (rgResolution === 'merge' && !CLIENT_CODE_RE.test(pickerCode(newRgAnchor))) {
        return 'Pick a target Relationship Group anchor from the list.';
      }
    }
    return null;
  }

  async function submit() {
    const v = validate();
    if (v) { setError(v); return; }
    // Disengagement follow-up answers are required (same rules everywhere).
    const dErrs = validateDisengagement(disengagement);
    if (Object.keys(dErrs).length) {
      setDisengErrors(dErrs);
      setError('Please complete the required disengagement questions below.');
      return;
    }
    setDisengErrors({});
    const payload = { clientName: member.name || null, disengagement };
    if (ctx.isSgParent) {
      payload.sgResolution = sgResolution;
      if (sgResolution === 'promote') {
        payload.newSgParent = newSgParent.trim();
      } else if (sgResolution === 'move') {
        payload.newSgParent = pickerCode(newSgParent);
      }
    }
    if (ctx.isRgAnchor) {
      payload.rgResolution = rgResolution;
      if (rgResolution === 'promote') {
        payload.newRgAnchor = newRgAnchor.trim();
      } else if (rgResolution === 'merge') {
        payload.newRgAnchor = pickerCode(newRgAnchor);
      }
    }

    // Optimistic: mark the client pending for the whole action lifecycle
    // (loading state on every row referencing it), close the modal, then
    // fire the request. Normally we ALSO dismiss every issue tied to the
    // client so it disappears immediately — but inside a multi-member group
    // (keepGroupVisible) removing one member rarely clears the whole issue,
    // so we keep the row visible (loading) and let the refetch reconcile.
    // Revert + toast on failure.
    markPending([member.code]);
    const hidden = keepGroupVisible ? [] : dismissByClientCode(member.code);
    close();
    const progressToastId = emitToast({
      kind: 'info',
      message: 'Calculating changes based on new groups…',
      durationMs: 0,
    });
    try {
      const data = await postResolveAction({
        verb:       'mark_nlc',
        clientCode: member.code,
        payload,
        testMode:   Boolean(testMode),
        sessionId:  testMode ? testSessionId : null,
      });
      // Sweep in selected related parties (marked NLC server-side). Skipped in
      // test mode — the batch endpoint writes real NLC rows the test-undo log
      // doesn't track. payload holds the answers (state was reset by close()).
      const related = payload.disengagement?.relatedParties || [];
      if (!testMode && related.length) {
        try {
          const r = await markRelatedParties({
            primaryContIndex:  data?.contIndex || null,
            primaryClientCode: member.code,
            period:            getActionPeriod(),
            sourceSectionKey:  'client-groups',
            disengagement:     payload.disengagement,
            relatedParties:    related,
          });
          emitToast({ kind: 'success', message: `Also marked ${r.marked.length} related part${r.marked.length === 1 ? 'y' : 'ies'} NLC.` });
        } catch (e2) {
          emitToast({ kind: 'error', message: `Related parties not marked: ${errorText(e2)}` });
        }
      }
      if (onResolved) await onResolved(data);
    } catch (e) {
      restoreIds(hidden);
      emitToast({
        kind: 'error',
        message: `Couldn't mark ${member.code} NLC: ${errorText(e)}. Restored.`,
      });
    } finally {
      clearPending([member.code]);
      dismissToast(progressToastId);
    }
  }

  return (
    <>
      <button
        type="button"
        className="cg-mark-nlc-btn"
        onClick={(e) => { e.stopPropagation(); setShowConfirm(true); }}
        title={`Mark ${member.code} as No Longer Client — excludes from all DI analysis.`}
      >
        Mark NLC
      </button>

      {showConfirm && (
        <div
          className="cg-resolve-overlay"
          onMouseDown={(e) => { overlayDownRef.current = (e.target === e.currentTarget); }}
          onMouseUp={(e) => {
            const onBackdrop = overlayDownRef.current && e.target === e.currentTarget;
            overlayDownRef.current = false;
            if (onBackdrop && !submitting) close();
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="cg-resolve-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cg-resolve-header">
              <h3>Mark as No Longer Client?</h3>
              <button
                type="button"
                className="cg-resolve-close"
                disabled={submitting}
                onClick={close}
                aria-label="Close"
              >×</button>
            </div>

            <div className="cg-resolve-summary">
              <div className="cg-resolve-summary-row">
                <ClientChip code={member.code} name={member.name} />
              </div>
            </div>

            <div className="cg-resolve-confirm">
              <p className="cg-resolve-confirm-body">
                <ClientChip code={member.code} name={member.name} /> will be excluded from all
                future data-integrity analysis. Open issues referencing
                this client will be auto-resolved.
              </p>

              {loading && <p className="cg-resolve-empty">Checking Signer Group / Relationship Group context…</p>}
              {ctxErr && (
                <div className="cg-resolve-error" role="alert">
                  <strong>Couldn't load context:</strong> {ctxErr}
                </div>
              )}

              {ctx && ctx.isSgParent && (
                <SgResolutionForm
                  ctx={ctx}
                  resolution={sgResolution}
                  setResolution={setSgResolution}
                  newSgParent={newSgParent}
                  setNewSgParent={setNewSgParent}
                  sgAnchors={sgAnchors}
                  disabled={submitting}
                />
              )}

              {ctx && ctx.isRgAnchor && (
                <RgResolutionForm
                  ctx={ctx}
                  resolution={rgResolution}
                  setResolution={setRgResolution}
                  newRgAnchor={newRgAnchor}
                  setNewRgAnchor={setNewRgAnchor}
                  rgAnchors={rgAnchors}
                  disabled={submitting}
                />
              )}

              {ctx && !ctx.isSgParent && !ctx.isRgAnchor && (
                <p className="cg-resolve-empty cg-resolve-simple-note">
                  This client is a standalone / child member — removing them
                  leaves their signer group and relationship group intact.
                </p>
              )}

              {!loading && !ctxErr && (
                <div className="cg-nlc-section" style={{ marginTop: 12 }}>
                  <p className="cg-resolve-confirm-body" style={{ marginBottom: 8 }}>
                    <strong>Disengagement details</strong> — answers below go to
                    Accounts Receivable so no separate disengagement form is needed.
                  </p>
                  <DisengagementFields
                    value={disengagement}
                    onChange={setDisengagement}
                    disabled={submitting}
                    errors={disengErrors}
                  />
                </div>
              )}

              {error && (
                <div className="cg-resolve-error" role="alert">
                  <strong>Couldn't mark NLC:</strong> {error}
                </div>
              )}

              <div className="cg-resolve-actions">
                <button
                  type="button"
                  className="cg-btn cg-btn--secondary"
                  disabled={submitting}
                  onClick={close}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="cg-btn cg-btn--danger"
                  disabled={submitting || loading || !!ctxErr || !ctx}
                  onClick={() => guard(submit)}
                >
                  {submitting ? 'Working…' : 'Yes, mark NLC'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SgResolutionForm({ ctx, resolution, setResolution, newSgParent, setNewSgParent, sgAnchors, disabled }) {
  // The 'promote' picker selects from this SG's own children, but the
  // 'move' picker draws from the global list of EXISTING SG parents
  // (excluding this client itself — we're about to NLC them, and they
  // can't be both the source and the target).
  const moveTargets = (sgAnchors || []).filter(a => a.clientCode !== ctx.clientCode);
  const childCount = ctx.sgChildren.length;
  return (
    <fieldset className="cg-resolve-field cg-resolve-field--radio cg-nlc-section">
      <legend className="cg-resolve-field-label">
        Signer Group — {ctx.clientCode} is the parent of {childCount}{' '}
        other member{childCount === 1 ? '' : 's'}. What happens to them?
        <span className="cg-resolve-required" aria-hidden="true"> *</span>
      </legend>

      <label className="cg-resolve-radio-row">
        <input
          type="radio" name="sgRes" value="promote"
          checked={resolution === 'promote'} disabled={disabled}
          onChange={() => setResolution('promote')}
        />
        <span>
          <strong>Promote a child</strong> to be the new Signer Group parent
          {resolution === 'promote' && (
            <select
              className="cg-nlc-subpicker"
              value={newSgParent}
              disabled={disabled}
              onChange={(e) => setNewSgParent(e.target.value)}
            >
              <option value="">— pick a child —</option>
              {ctx.sgChildren.map((c) => (
                <option key={c.clientCode} value={c.clientCode}>
                  {c.clientCode}{c.clientName ? ` — ${c.clientName}` : ''}
                </option>
              ))}
            </select>
          )}
        </span>
      </label>

      <label className="cg-resolve-radio-row">
        <input
          type="radio" name="sgRes" value="disband"
          checked={resolution === 'disband'} disabled={disabled}
          onChange={() => setResolution('disband')}
        />
        <span><strong>Disband the Signer Group</strong> — each remaining member becomes its own standalone Signer Group</span>
      </label>

      <label className="cg-resolve-radio-row">
        <input
          type="radio" name="sgRes" value="move"
          checked={resolution === 'move'} disabled={disabled}
          onChange={() => setResolution('move')}
        />
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <strong>Move all members</strong> into another existing Signer Group:
          <GroupAnchorPicker
            anchors={moveTargets}
            value={resolution === 'move' ? newSgParent : ''}
            disabled={disabled || resolution !== 'move'}
            onChange={(v) => { setNewSgParent(v); if (resolution !== 'move') setResolution('move'); }}
            onFocus={() => { if (resolution !== 'move') setResolution('move'); }}
          />
        </span>
      </label>
    </fieldset>
  );
}

function RgResolutionForm({ ctx, resolution, setResolution, newRgAnchor, setNewRgAnchor, rgAnchors, disabled }) {
  // 'merge' draws from the global RG-anchor list, minus the client being marked
  // (it IS the anchor we're tearing down — dgNlcContext returns no sgParentCode).
  const mergeTargets = (rgAnchors || []).filter(a => a.clientCode !== ctx.clientCode);
  const sibCount = ctx.rgSiblingSgs.length;
  return (
    <fieldset className="cg-resolve-field cg-resolve-field--radio cg-nlc-section">
      <legend className="cg-resolve-field-label">
        Relationship Group — {ctx.clientCode}'s Signer Group anchors a Relationship Group with{' '}
        {sibCount} other Signer Group{sibCount === 1 ? '' : 's'}. What happens?
        <span className="cg-resolve-required" aria-hidden="true"> *</span>
      </legend>

      <label className="cg-resolve-radio-row">
        <input
          type="radio" name="rgRes" value="promote"
          checked={resolution === 'promote'} disabled={disabled}
          onChange={() => setResolution('promote')}
        />
        <span>
          <strong>Promote a sibling Signer Group</strong> to be the new Relationship Group anchor
          {resolution === 'promote' && (
            <select
              className="cg-nlc-subpicker"
              value={newRgAnchor}
              disabled={disabled}
              onChange={(e) => setNewRgAnchor(e.target.value)}
            >
              <option value="">— pick a sibling Signer Group —</option>
              {ctx.rgSiblingSgs.map((s) => (
                <option key={s.sgCode} value={s.sgCode}>
                  {s.sgCode}{s.sgName ? ` — ${s.sgName}` : ''} ({s.memberCount} member{s.memberCount === 1 ? '' : 's'})
                </option>
              ))}
            </select>
          )}
        </span>
      </label>

      <label className="cg-resolve-radio-row">
        <input
          type="radio" name="rgRes" value="disband"
          checked={resolution === 'disband'} disabled={disabled}
          onChange={() => setResolution('disband')}
        />
        <span><strong>Disband the Relationship Group</strong> — each sibling Signer Group becomes its own standalone Relationship Group</span>
      </label>

      <label className="cg-resolve-radio-row">
        <input
          type="radio" name="rgRes" value="merge"
          checked={resolution === 'merge'} disabled={disabled}
          onChange={() => setResolution('merge')}
        />
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <strong>Merge with another Relationship Group</strong>, anchored on:
          <GroupAnchorPicker
            anchors={mergeTargets}
            value={resolution === 'merge' ? newRgAnchor : ''}
            disabled={disabled || resolution !== 'merge'}
            onChange={(v) => { setNewRgAnchor(v); if (resolution !== 'merge') setResolution('merge'); }}
            onFocus={() => { if (resolution !== 'merge') setResolution('merge'); }}
          />
        </span>
      </label>
    </fieldset>
  );
}
